import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { PLGrid } from '../../components/common/PLGrid';
import { GridResetButton } from '../../components/common/GridResetButton';
import ExportAgCSVButton from '../../components/common/ExportAgCSVButton';
import { serialNumberColumn } from '../../libs/getSerialNumberColumn';
import { useGridData } from '../../hooks/useGridData';
import { loginHistoryConfig } from './loginHistory.config';
import { userConfig } from '../users/user.config';
import { LoginHistoryRecord } from '../../types/auth.types';
import { User } from '../../types/users.types';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faUser, faHistory, faSignOutAlt, faClock } from '@fortawesome/free-solid-svg-icons';
import { Card, Col, Row, Spin } from 'antd';
import { calculateTotalSessionTime } from './loginHistory.config';
import { ID_OBJ } from '../../types/common.types';

const LoginHistoryPage: React.FC = () => {
  const gridRef = useRef<AgGridReact>(null);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [userDetails, setUserDetails] = useState<User | null>(null);
  const [loadingUser, setLoadingUser] = useState(false);

  // Fetch user details from the API
  useEffect(() => {
    if (id) {
      setLoadingUser(true);
      userConfig.api.getOne(id)
        .then((response) => setUserDetails(response.data))
        .finally(() => setLoadingUser(false));
    }
  }, [id]);
 
  const perms = { id: id || "" };
  const { rowData, loading } = useGridData<LoginHistoryRecord,ID_OBJ>(loginHistoryConfig.api.list,perms);

  const columnDefs = useMemo(() => [
    serialNumberColumn,
    ...loginHistoryConfig.loginHistoryTableColumns,
  ], []);

  // Stats for the boxes
  const stats = [
    {
      title: 'Total Session Time',
      value: rowData?.length ? calculateTotalSessionTime(rowData) : '0H:0M',
      icon: <FontAwesomeIcon icon={faClock} className="text-purple-500" />,
    },
    {
      title: 'Total Login History',
      value: rowData?.length || 0,
      icon: <FontAwesomeIcon icon={faHistory} className="text-green-500" />,
    },
    {
      title: 'Manual Logouts',
      value: rowData?.filter(record => record.logoutTime).length || 0,
      icon: <FontAwesomeIcon icon={faSignOutAlt} className="text-red-500" />,
    },
    {
      title: 'Auto Logouts',
      value: rowData?.filter(record => !record.logoutTime && new Date(record.loginTime).getTime() + 3 * 60 * 60 * 1000 < Date.now()).length || 0,
      icon: <FontAwesomeIcon icon={faClock} className="text-orange-500" />,
    },
  ];

  return (
    <div className="h-full flex flex-col">
      {/* Header with Back Button */}
      <div className="flex justify-between items-center border-b pb-4 mb-6">
        <div className="flex items-center gap-3">
          <FontAwesomeIcon
            icon={faChevronLeft}
            className="text-gray-600 hover:text-black text-xl cursor-pointer"
            title="Go Back To User List"
            onClick={() => navigate(-1)} // Navigate back
          />
          <h2 className="text-xl font-semibold m-0">
            Login History of {userDetails?.fullName || 'User'} ({userDetails?.email || 'N/A'})
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <ExportAgCSVButton gridRef={gridRef} fileName={`${loginHistoryConfig.entityName}List`} />
          <GridResetButton gridRef={gridRef} />
        </div>
      </div>

      {/* Stats Section */}
      <Row gutter={[24, 24]} className="mb-6">
        {stats.map((stat, index) => (
          <Col xs={24} sm={12} md={6} key={index}>
            <Card>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 14, color: '#888' }}>{stat.title}</div>
                  <div style={{ fontSize: 24, fontWeight: 600 }}>{stat.value}</div>
                </div>
                <div style={{ fontSize: 28 }}>
                  {stat.icon}
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Table */}
      <div className="flex-1 overflow-x-auto">
        <PLGrid
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default LoginHistoryPage;